<!-- Performance Evaluation -->
<template>
  <div class="performance-page">

    <!-- 상단 제목 -->
    <div class="page-header">
      <div>
        <h2>FC 실적평가</h2>
        <p>FC 3개년 월별 실적 현황</p>
      </div>

      <div class="search-area">
        <!-- <label>기준 연도</label>
        <select v-model.number="baseYear">
          <option
            v-for="year in yearOptions"
            :key="year"
            :value="year"
          >
            {{ year }}년
          </option>
        </select> -->
        <div class="fc-button-group">
          <template v-if="!isFcDirector">
            <button
              type="button"
              :class="{ active: selectedHeadOffice === 'ALL' }"
              @click="selectedHeadOffice = 'ALL'"
            >
              [00] 전체
            </button>

            <button
              v-for="office in headOfficeList"
              :key="office.code"
              type="button"
              :class="{ active: selectedHeadOffice === office.code }"
              @click="selectedHeadOffice = office.code"
            >
              [{{ office.code }}] {{ office.name }}
            </button>
          </template>

          <!-- 본부장 권한: 본인 소속 본부만 표시 -->
          <template v-else>
            <button
              v-for="office in myHeadOfficeList"
              :key="office.code"
              type="button"
              class="active"
              disabled
            >
              [{{ office.code }}] {{ office.name }}
            </button>
          </template>
        </div>

        <!-- <label>FC 선택</label>
        <select v-model="selectedHeadOffice">
          <option value="ALL">[00] 전체 </option>
          <option
            v-for="office in headOfficeList"
            :key="office.code"
            :value="office.code"
          >
            [{{ office.code }}] {{ office.name }}
          </option>
        </select> -->
      </div>
    </div>

    <!-- 연도 안내 -->
    <!-- <div class="year-summary">
      <div class="year-badge">
        {{ baseYear - 2 }}년
      </div>

      <span class="year-arrow">→</span>

      <div class="year-badge">
        {{ baseYear - 1 }}년
      </div>

      <span class="year-arrow">→</span>

      <div class="year-badge current">
        {{ baseYear }}년
      </div>

      <span class="summary-text">
        3개년 실적 비교
      </span>
    </div> -->

    <!-- FC별 영역 -->
    <section
      v-for="office in filteredHeadOffices"
      :key="office.code"
      class="office-section"
    >

      <!-- FC 제목 -->
      <div class="office-header">
        <div class="office-title">
          <span class="office-label">FC</span>

          <h3>{{ office.name }}</h3>

          <span class="manager-name">
            현 FC : {{ this.getMonthlyData(office, baseYear, baseMonth).SHOP_CEO_NM || "-" }}
          </span>
        </div>

        <div class="office-info">
          <span>소속 매장 {{ office.storeCount }}개</span>
        </div>
      </div>

      <!-- 매장별 표 -->
      <div class="table-scroll">

        <table class="performance-table">

          <thead>
            <!-- 연도 -->
            <tr class="year-header">
              <th
                rowspan="2"
                class="fixed-no"
              >
                번호
              </th>

              <th
                rowspan="2"
                class="fixed-item"
              >
                항목
              </th>

              <th
                v-for="year in years"
                :key="year"
                colspan="13"
                class="year-group"
                :class="`year-color-${years.indexOf(year)}`"
              >
                {{ year }}년
              </th>
            </tr>

            <!-- 월 -->
            <tr class="month-header">
              <template
                v-for="year in years"
                :key="year"
              >
                <th
                  v-for="month in 12"
                  :key="`${year}-${month}`"
                  class="month-cell"
                  :class="{ 'current-year': year === baseYear }"
                >
                  {{ month }}월
                </th>

                <!-- 연도별 합계 -->
                <th
                  class="month-cell year-total-header"
                  :class="`year-color-${years.indexOf(year)}`"
                >
                 {{ year }}년 합계
                </th>
              </template>
            </tr>

          </thead>

          <tbody>
            <tr
              v-for="metric in metrics"
              :key="metric.no"
              :class="{
                'blank-row': metric.type === 'blank'
              }"
            >
              <td class="number-cell">
                {{ metric.no }}
              </td>

              <td class="item-cell">
                {{ metric.name }}
              </td>

              <template
                v-for="(year, yearIndex) in years"
                :key="year"
              >
                <td
                  v-for="month in 12"
                  :key="`${metric.no}-${year}-${month}`"
                  class="value-cell"
                  :class="[
                    { 'current-year': year === baseYear },
                    metric.valueClass || '',
                    metric.no === 20 ? `year-color-${yearIndex}` : '',
                    [7, 8, 18, 19].includes(metric.no) ? 'bg-highlight' : '',
                    getDifferenceClass(office, metric, year, month)
                  ]"
                >
                  <!-- 20번 FC수익 -->
                  <template v-if="metric.no === 20">
                    <input
                      v-if="canEditFcProfit(office)"
                      type="number"
                      class="fc-profit-input"
                      :value="
                        fcProfitInputs[
                          getFcProfitKey(office, year, month)
                        ] ?? (
                          getMonthlyData(office, year, month).FC_PROFIT ?? ''
                        )
                      "
                      @input="
                        onFcProfitInput(
                          office,
                          year,
                          month,
                          $event.target.value
                        )
                      "
                      @blur="saveFcProfit(office, year, month)"
                      placeholder="입력"
                    />

                    <template v-else>
                      {{
                        getMonthlyData(office, year, month).FC_PROFIT == null
                          ? "-"
                          : formatNumber(
                              Number(getMonthlyData(office, year, month).FC_PROFIT)
                            )
                      }}
                    </template>
                  </template>

                  <!-- 나머지 항목 -->
                  <template v-else>
                    {{ getMetricValue(office, metric, year, month) }}
                  </template>
                </td>

                  <!-- 연도별 합계 -->
                <td
                  class="value-cell year-total-cell"
                  :class="[
                    metric.type !== 'blank' ? `year-color-${yearIndex}` : '',
                    { 'no-background': metric.type === 'blank' }
                  ]"
                >
                  {{ getYearTotal(office, metric, year) }}
                </td>
              </template>
            </tr>
          </tbody>

        </table>
      </div>

      <!-- 매장별 상세 -->
      <!-- <div class="store-list">
        <div class="store-list-header">
          <h4>소속 매장별 실적</h4>
          <span>
            {{ office.stores.length }}개 매장
          </span>
        </div>

        <div
          v-for="store in office.stores"
          :key="store.code"
          class="store-row"
        >
          <div class="store-name">
            <span class="store-code">{{ store.code }}</span>
            <strong>{{ store.name }}</strong>
          </div>

          <div class="store-stats">
            <div>
              <span>당월 판매수량</span>
              <strong>
                {{ formatNumber(store.monthlySales) }}
              </strong>
            </div>

            <div>
              <span>연간 판매수량</span>
              <strong>
                {{ formatNumber(store.yearlySales) }}
              </strong>
            </div>

            <div>
              <span>연간 순위</span>
              <strong>{{ store.yearlyRank }}위</strong>
            </div>
          </div>
        </div>
      </div> -->

    </section>

    <!-- 데이터가 없을 경우 -->
    <div v-if="isLoading" class="empty-message">
      FC 실적을 불러오는 중입니다...
    </div>

    <div
      v-else-if="loadError"
      class="empty-message error-message"
    >
      {{ loadError }}
    </div>

    <div
      v-else-if="filteredHeadOffices.length === 0"
      class="empty-message"
    >
      조회할 FC 데이터가 없습니다.
    </div>

    <div class="page-footer">
      ※ 실적 자료는 FC 별로 집계됩니다.
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "HeadOfficePerformance",

  data() {
    return {
      baseYear: new Date().getFullYear(),
      baseMonth : new Date().getMonth() + 1,
      selectedHeadOffice: "ALL",
      headOfficeList: [],
      monthlyDataByKey: {},
      isLoading: false,
      loadError: "",
      userInfo:null,
      
      fcProfitInputs: {},
      fcProfitSaving: {},
      fcProfitTimers: {},

      metrics: [
        { no: 1, name: "FC명", type: "text" },
        { no: 2, name: "사업자수", type: "number" },
        { no: 3, name: "계약수량(전체)", type: "number" },
        { no: 4, name: "월계약수량 누적합", type: "number" },
        { no: 5, name: "월별계약수량", type: "number" },
        { no: 6, name: "월판매수량", type: "number" },
        { no: 7, name: "수량차이", type: "number" },        // (6-5)
        { no: 8, name: "달성률", type: "percent" },         // (6/5 *100)
        { no: 9, name: "FC비중", type: "percent" },         // (5/3 *100)
        { no: 10, name: "당월순위", type: "rank" },         // 달성률로 순위 getMonthlyRank()
        { no: 11, name: "월1등차이률", type: "percent" },
        { no: 12, name: "연간순위", type: "rank" },         // 누적달성률 = (1월~해당월 누적판매수량/1월~해당월 누적 계약수량)* 100
        { no: 13, name: "연1등차이률", type: "percent" },
        { no: 14, name: "", type: "blank" },
        { no: 15, name: "최고수량", type: "number" },
        { no: 16, name: "연최고수량 누적합", type: "number" },
        { no: 17, name: "판매수량", type: "number" },
        { no: 18, name: "최고차이", type: "number" },       // (17-15)
        { no: 19, name: "최고달률", type: "percent" },      // (17/15 *100)
        { no: 20, name: "FC수익", type: "number" },
      ],
    };
  },

  created() {
    const userInfoString = localStorage.getItem("userInfo");
    if (userInfoString) {
      this.userInfo = JSON.parse(userInfoString);
    }
  },

  watch: {
    userInfo: {
      immediate: true,
      handler(user) {
        if (
          user?.EMP_DEPT_NM === "판매본부" &&
          user?.EMP_CLASS_NM === "본부장"
        ) {
          this.selectedHeadOffice = String(user.BRANCH_CD);
        }
      },
    },
  },

  computed: {
    years() {
      return [
        this.baseYear - 2,
        this.baseYear - 1,
        this.baseYear,
      ];
    },

    filteredHeadOffices() {
      if (this.selectedHeadOffice === "ALL") {
        return this.headOfficeList;
      }

      return this.headOfficeList.filter(
        office => office.code === this.selectedHeadOffice
      );
    },

    isFcDirector() {
      return (
        this.userInfo?.EMP_DEPT_NM === "판매본부" &&
        this.userInfo?.EMP_CLASS_NM === "본부장"
      );
    },

    myHeadOfficeList() {
      if (!this.isFcDirector) {
        return this.headOfficeList;
      }

      const myBranchCd = String(this.userInfo?.BRANCH_CD ?? "").trim();

      return this.headOfficeList.filter(
        office => String(office.code).trim() === myBranchCd
      );
    },
  },

  mounted() {
    this.loadPerformanceData();
  },

  methods: {
    async loadPerformanceData() {
      this.isLoading = true;
      this.loadError = "";
      try {
        const response = await axios.get("/api/peListPage/details");
        this.baseYear = Number(response.data.currentYear);
        this.headOfficeList = response.data.branches.map((branch) => ({
          code: branch.BRANCH_CD,
          name: branch.BRANCH_NM,
          storeCount: Number(branch.STORE_COUNT) || 0,
        }));
        this.monthlyDataByKey = Object.fromEntries(
          response.data.monthlyData.map((row) => [
            `${row.BRANCH_CD}:${row.SALE_MM}`,
            row,
          ]),
        );
      } catch (error) {
        console.error("FC 실적을 불러오지 못했습니다.", error);
        this.loadError =
          error.response?.data?.error || "FC 실적을 불러오지 못했습니다.";
      } finally {
        this.isLoading = false;
      }
    },

    getSaleMm(year, month) {
      return `${year}${String(month).padStart(2, "0")}`;
    },

    getMonthlyData(office, year, month) {
      return (
        this.monthlyDataByKey[
          `${office.code}:${this.getSaleMm(year, month)}`
        ] || {
          SALE_QTY: null,
          DCM_SALE_AMT: null,
          MAX_QTY: null,
          SHOP_CEO_NM: null,
          FC_PROFIT: null,
          CONTRACT_QTY: null,
        }
      );
    },

    getMonthlySales(office, year, month) {
      const value = this.getMonthlyData(office, year, month).SALE_QTY;
      return value === null || value === undefined ? null : Number(value);
    },

    getMonthlyContract(office, year, month) {
      const value = this.getMonthlyData(office, year, month).CONTRACT_QTY;
      return value === null || value === undefined ? null : Number(value);
    },

    getMonthlyMax(office, year, month) {
      const value = this.getMonthlyData(office, year, month).MAX_QTY;
      return value === null || value === undefined ? null : Number(value);
    },

    hasSalesDataThrough(office, year, month) {
      return Array.from({ length: month }, (_, index) => {
        const value = this.getMonthlyData(office, year, index + 1).SALE_QTY;
        return value !== null && value !== undefined;
      }).every(Boolean);
    },

    getYearToDateTotal(office, year, month, field) {
      const values = Array.from({ length: month }, (_, index) =>
        this.getMonthlyData(office, year, index + 1)[field],
      );
      if (
        field === "SALE_QTY" &&
        values.some((value) => value === null || value === undefined)
      ) {
        return null;
      }
      if (
        field === "CONTRACT_QTY" &&
        values.every((value) => value === null || value === undefined)
      ) {
        return null;
      }
      return values.reduce((sum, value) => sum + (Number(value) || 0), 0);
    },

    getAnnualTotal(office, year, field) {
      const months = year === this.baseYear
        ? new Date().getMonth() + 1
        : 12;
      return this.getYearToDateTotal(office, year, months, field);
    },

    getMonthlyAchievement(office, year, month) {
      const sales = this.getMonthlySales(office, year, month);
      const contractQty = this.getMonthlyContract(office, year, month);
      return sales !== null &&
        Number.isFinite(contractQty) &&
        contractQty > 0
        ? (sales / contractQty) * 100
        : null;
    },

    getYearToDateAchievement(office, year, month) {
      const sales = this.getYearToDateTotal(office, year, month, "SALE_QTY");
      const contractQty = this.getYearToDateTotal(
        office, year, month, "CONTRACT_QTY",
      );
      return sales !== null && contractQty > 0
        ? (sales / contractQty) * 100
        : null;
    },

    getOverallContractTotal(year, month) {
      const quantities = this.headOfficeList.map((office) =>
        this.getMonthlyContract(office, year, month),
      ).filter((quantity) => quantity !== null);
      return quantities.length
        ? quantities.reduce((total, quantity) => total + quantity, 0)
        : null;
    },

    getOverallContractYearToDateTotal(year, month) {
      const quantities = this.headOfficeList.map((office) =>
        this.getYearToDateTotal(office, year, month, "CONTRACT_QTY"),
      ).filter((quantity) => quantity !== null);
      return quantities.length
        ? quantities.reduce((total, quantity) => total + quantity, 0)
        : null;
    },
    // 10번: 당월순위 // 당월 달성률이 높은 FC부터 순위 부여
    getMonthlyRank(office, year, month) {
      const achievement = this.getMonthlyAchievement(office, year, month);
      if (achievement === null) {
        return null;
      }
      return this.headOfficeList.filter(
        (candidate) => {
          const candidateAchievement = this.getMonthlyAchievement(
            candidate, year, month,
          );
          return candidateAchievement !== null &&
            candidateAchievement > achievement;
        },
      ).length + 1;
    },
    
    // 12번: 연간순위 // 1월부터 해당 월까지 누적 달성률이 높은 FC부터 순위 부여
    getAnnualRank(office, year, month) {
      const achievement = this.getYearToDateAchievement(office, year, month);
      if (achievement === null) {
        return null;
      }
      return this.headOfficeList.filter(
        (candidate) => {
          const candidateAchievement = this.getYearToDateAchievement(
            candidate, year, month,
          );
          return candidateAchievement !== null &&
            candidateAchievement > achievement;
        },
      ).length + 1;
    },

    getAchievementDifference(achievement, leaderAchievement) {
      return Number.isFinite(achievement) &&
        Number.isFinite(leaderAchievement)
        ? achievement - leaderAchievement
        : null;
    },

    getMetricValue(office, metric, year, month) {
      if (metric.type === "blank") {
        return "";
      }
      if (
        year === this.baseYear &&
        month > new Date().getMonth() + 1 &&
        metric.no !== 2 &&
        metric.no !== 3 &&
        metric.no !== 15
      ) {
        return "-";
      }
      if (metric.no === 1) {
        return this.getMonthlyData(office, year, month).SHOP_CEO_NM || "-";
      }

      const monthly = this.getMonthlyData(office, year, month);
      const salesQty = this.getMonthlySales(office, year, month);
      let value;

      switch (metric.no) {
        case 2:
          value = office.storeCount;
          break;
        case 3:
          value = this.getOverallContractTotal(year, month);
          break;
        case 4:
          value = this.getYearToDateTotal(
            office, year, month, "CONTRACT_QTY",
          );
          break;
        case 5:
          value = monthly.CONTRACT_QTY;
          break;
        case 6:
          value = salesQty;
          break;
        case 7: {
          const contractQty = this.getMonthlyContract(office, year, month);
          value = salesQty !== null && contractQty !== null
            ? salesQty - contractQty
            : null;
          break;
        }
        case 8: {
          const contractQty = this.getMonthlyContract(office, year, month);
          value = salesQty !== null &&
            contractQty !== null &&
            contractQty > 0
            ? (salesQty / contractQty) * 100
            : null;
          break;
        }
        case 9: {
          const overallContractQty = this.getOverallContractTotal(year, month);
          const contractQty = this.getMonthlyContract(office, year, month);
          value = overallContractQty > 0 && contractQty !== null
            ? (contractQty / overallContractQty) * 100
            : null;
          break;
        }
        case 10:
          value = this.getMonthlyRank(office, year, month);
          break;
        case 11: {
          const achievement = this.getMonthlyAchievement(
            office, year, month,
          );
          const leaderAchievement = Math.max(
            ...this.headOfficeList
              .map((candidate) =>
                this.getMonthlyAchievement(candidate, year, month),
              )
              .filter((rate) => rate !== null),
          );
          value = this.getAchievementDifference(
            achievement, leaderAchievement,
          );
          break;
        }
        case 12:
          value = this.getAnnualRank(office, year, month);
          break;
        case 13: {
          const achievement = this.getYearToDateAchievement(
            office, year, month,
          );
          const leaderAchievement = Math.max(
            ...this.headOfficeList
              .map((candidate) =>
                this.getYearToDateAchievement(candidate, year, month),
              )
              .filter((rate) => rate !== null),
          );
          value = this.getAchievementDifference(
            achievement, leaderAchievement,
          );
          break;
        }
        case 15:
          value = this.getMonthlyMax(office, year, month);
          break;
        case 16:
          value = this.getYearToDateTotal(
            office, year, month, "MAX_QTY",
          );
          break;
        case 17:
          value = salesQty;
          break;
        case 18: {
          const maxQty = this.getMonthlyMax(office, year, month);
          value = salesQty !== null && maxQty !== null
            ? salesQty - maxQty
            : null;
          break;
        }
        case 19: {
          const maxQty = this.getMonthlyMax(office, year, month);
          value = salesQty !== null && salesQty > 0 && maxQty !== null
            ? (salesQty/ maxQty) * 100
            : null;
          break;
        }
        default:
          return "";
      }

      if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
      }
      if (metric.type === "percent") {
        return `${Number(value).toFixed(2)}%`;
      }
      if (metric.type === "rank") {
        return `${value}위`;
      }
      if (metric.type === "number") {
        return this.formatNumber(value);
      }
      return value;
    },

    formatNumber(value) {
      if (value === null || value === undefined) {
        return "-";
      }
      return Number(value).toLocaleString("ko-KR");
    },

    getYearTotal(office, metric, year) {
      if (metric.type === "blank") {
        return "";
      }
      const totalMonths = year === this.baseYear
        ? new Date().getMonth() + 1
        : 12;
      // if (metric.no === 1) {
      //   return this.getMonthlyData(office, year, totalMonths).SHOP_CEO_NM || "-";
      // }
      if (metric.no === 2) {
        return this.formatNumber(office.storeCount);
      }
      if (metric.no === 10 || metric.no === 11) {
        return "-";
      }
      if (metric.no === 12) {
        const rank = this.getAnnualRank(office, year, totalMonths);
        return rank === null ? "-" : `${rank}위`;
      }
      if (metric.no === 13) {
        const achievement = this.getYearToDateAchievement(
          office, year, totalMonths,
        );
        const leaderAchievement = Math.max(
          ...this.headOfficeList
            .map((candidate) =>
              this.getYearToDateAchievement(candidate, year, totalMonths),
            )
            .filter((rate) => rate !== null),
        );
        const difference = this.getAchievementDifference(
          achievement, leaderAchievement,
        );
        return difference === null ? "-" : `${difference.toFixed(2)}%`;
      }

      const contractTotal = this.getYearToDateTotal(
        office, year, totalMonths, "CONTRACT_QTY",
      );
      const overallContractTotal = this.getOverallContractYearToDateTotal(
        year, 12,
      );
      const salesTotal = this.getYearToDateTotal(
        office, year, totalMonths, "SALE_QTY",
      );
      const maxTotal = this.getYearToDateTotal(
        office, year, totalMonths, "MAX_QTY",
      );
      let value;

      switch (metric.no) {
        case 3:
          value = overallContractTotal;
          break;
        case 4:
        case 5:
          value = contractTotal;
          break;
        case 6:
        case 17:
          value = salesTotal;
          break;
        case 7:
          value = contractTotal !== null && salesTotal !== null
            ? salesTotal - contractTotal
            : null;
          break;
        case 8:
          value = contractTotal > 0 && salesTotal !== null
            ? (salesTotal / contractTotal) * 100
            : null;
          break;
        case 9:
          value = overallContractTotal > 0 && contractTotal !== null
            ? (contractTotal / overallContractTotal) * 100
            : null;
          break;
        case 15:
          value = this.getYearToDateTotal(
            office, year, 12, "MAX_QTY"
          );
          break;
        case 16:
          value = maxTotal;
          break;
        case 18:
          value = maxTotal !== null && salesTotal !== null
            ? salesTotal - maxTotal
            : null;
          break;
        case 19:
          value = maxTotal !== null && salesTotal > 0
            ? (salesTotal / maxTotal) * 100
            : null;
          break;
        default:
          return "";
      }

      if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
      }
      if (metric.type === "percent") {
        return `${value.toFixed(2)}%`;
      }
      if (metric.type === "rank") {
        return `${value}위`;
      }
      return this.formatNumber(value);
    },

    getDifferenceClass(office, metric, year, month) {
      // 7번과 18번 항목만 적용
      if (![7, 18].includes(metric.no)) {
        return '';
      }

      const value = this.getMetricValue(office, metric, year, month);

      // 값이 없거나 '-'인 경우 제외
      if (value === null || value === undefined || value === '-') {
        return '';
      }

      // 쉼표가 포함된 숫자도 처리
      const numberValue = Number(String(value).replace(/,/g, ''));

      if (!Number.isFinite(numberValue) || numberValue === 0) {
        return '';
      }

      return numberValue < 0 ? 'negative-value' : 'positive-value';
    },

    // FC수익 입력 권한
    canEditFcProfit(office) {
      return (
        this.userInfo.EMP_DEPT_NM === "판매본부" &&
        this.userInfo.EMP_CLASS_NM === "본부장" &&
        String(this.userInfo.BRANCH_CD) === String(office.code)
      );
    },
    
    // FC수익 입력값 변경
    onFcProfitInput(office, year, month, value) {
      if (!this.canEditFcProfit(office)) {
        return;
      }

      const key = this.getFcProfitKey(office, year, month);

      // 입력값 반영
      this.fcProfitInputs[key] = value;

      // 기존 자동 저장 타이머 제거
      if (this.fcProfitTimers[key]) {
        clearTimeout(this.fcProfitTimers[key]);
      }

      // 700ms 동안 추가 입력이 없으면 자동 저장
      this.fcProfitTimers[key] = setTimeout(() => {
        this.saveFcProfit(office, year, month);
      }, 700);
    },

    // FC수익 자동 저장
    async saveFcProfit(office, year, month) {
      if (!this.canEditFcProfit(office)) {
        return;
      }

      const key = this.getFcProfitKey(office, year, month);

      // 타이머 제거
      if (this.fcProfitTimers[key]) {
        clearTimeout(this.fcProfitTimers[key]);
        delete this.fcProfitTimers[key];
      }

      const inputValue = this.fcProfitInputs[key];

      // 빈 값은 저장하지 않음
      if (
        inputValue === "" ||
        inputValue === null ||
        inputValue === undefined
      ) {
        return;
      }

      const profit = Number(inputValue);

      if (!Number.isFinite(profit)) {
        return;
      }

      // 저장 중이면 중복 요청 방지
      if (this.fcProfitSaving[key]) {
        return;
      }

      this.fcProfitSaving[key] = true;

      try {
        await axios.post("/api/peListPage/profit/save", {
          SALE_MM: this.getSaleMm(year, month),
          BRANCH_CD: office.code,
          FC_PROFIT: profit,
          userInfo: this.userInfo,
        });

        // 저장된 데이터 화면 반영
        const monthlyKey =
          `${office.code}:${this.getSaleMm(year, month)}`;

        if (this.monthlyDataByKey[monthlyKey]) {
          this.monthlyDataByKey[monthlyKey].FC_PROFIT = profit;
        }

        console.log("FC수익 자동 저장 완료:", key);

      } catch (error) {
        console.error("FC수익 자동 저장 오류:", error);

        alert(
          error.response?.data?.message ||
          "FC수익 저장 중 오류가 발생했습니다."
        );

      } finally {
        this.fcProfitSaving[key] = false;
      }
    },

    getFcProfitKey(office, year, month) {
      return `${office.code}:${this.getSaleMm(year, month)}`;
    },

    getFcProfitInput(office, year, month) {
      const key = this.getFcProfitKey(office, year, month);

      return this.fcProfitInputs[key] ?? "";
    },

  },
};
</script>

<style scoped>
.performance-page {
  min-height: 100vh;
  padding: 10px;
  background: #f3f5f8;
  color: #252b35;
  font-family: "Malgun Gothic", "맑은 고딕", sans-serif;
  box-sizing: border-box;
}

/* 상단 */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.page-header h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #6c7480;
  font-size: 13px;
}

.search-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* .search-area label {
  font-size: 13px;
  font-weight: 600;
}

.search-area select {
  min-width: 110px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #cbd2dc;
  border-radius: 4px;
  background: #fff;
} */

.fc-button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fc-button-group button {
  padding: 6px 12px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-family: "Malgun Gothic", "맑은 고딕", sans-serif;
  font-size: 14px;
}

.fc-button-group button.active {
  background: #1976d2;
  color: #fff;
  border-color: #1976d2;
}

/* 연도 안내 */
.year-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  background: #fff;
  border: 1px solid #dce1e8;
  border-radius: 5px;
}

.year-badge {
  padding: 7px 14px;
  border: 1px solid #ccd4df;
  border-radius: 4px;
  background: #edf1f6;
  font-size: 13px;
  font-weight: 700;
}

.year-badge.current {
  background: #2f5f95;
  border-color: #2f5f95;
  color: #fff;
}

.year-arrow {
  color: #8a929e;
}

.summary-text {
  margin-left: auto;
  color: #5d6878;
  font-size: 13px;
}

/* FC 영역 */
.office-section {
  margin-bottom: 10px;
  padding: 10px;
  background: #fff;
  border: 1px solid #dce1e8;
  border-radius: 5px;
}

.office-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 5px;
  padding-bottom: 6px;
  border-bottom: 2px solid #36475d;
}

.office-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.office-label {
  padding: 4px 8px;
  background: #e8edf4;
  border-radius: 3px;
  color: #40536d;
  font-size: 12px;
  font-weight: 700;
}

.office-title h3 {
  margin: 0;
  font-size: 19px;
}

.manager-name {
  color: #536174;
  font-size: 13px;
}

.office-info {
  color: #667181;
  font-size: 12px;
  white-space: nowrap;
}

/* 표 가로 스크롤 */
.table-scroll {
  width: 100%;
  overflow-x: auto;
  position: relative;
  border: 1px solid #bfc8d4;
}

/* 실적표 */
.performance-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.performance-table th,
.performance-table td {
  min-width: 78px;
  height: 30px;
  padding: 5px 7px;
  border-right: 1px solid #d0d6df;
  border-bottom: 1px solid #d0d6df;
  box-sizing: border-box;
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
}

.performance-table th {
  font-weight: 700;
}

/* 번호/항목 열 */
.performance-table .fixed-no,
.performance-table .number-cell {
  position: sticky;
  left: 0;
  z-index: 5;
  width: 48px;
  min-width: 48px;
  background: #e4eaf2;
}

.performance-table .fixed-item,
.performance-table .item-cell {
  position: sticky;
  left: 48px;
  z-index: 5;
  width: 135px;
  min-width: 135px;
  background: #e4eaf2;
}

.performance-table .year-header th {
  height: 30px;
  background: #dbe4ef;
  color: #26384e;
  font-size: 14px;
}

.performance-table .year-header .fixed-no,
.performance-table .year-header .fixed-item {
  z-index: 7;
}

.performance-table .month-header th {
  height: 30px;
  background: #edf1f6;
}

.performance-table .manager-header th {
  height: 30px;
  background: #f7f8fa;
  color: #5b6676;
  font-size: 11px;
}

/* 첫 번째 연도 */
.performance-table .year-header .year-color-0 {
  background: #f5dfbd !important;
}

/* 두 번째 연도 */
.performance-table .year-header .year-color-1 {
  background: #cce8d5 !important;
}

/* 세 번째 연도 */
.performance-table .year-header .year-color-2 {
  background: #c8daf0 !important;
}

/* FC수익 - 첫 번째 연도 */
.performance-table tbody .year-color-0 {
  background-color: #f5dfbd !important;
}

/* FC수익 - 두 번째 연도 */
.performance-table tbody .year-color-1 {
  background-color: #cce8d5 !important;
}

/* FC수익 - 세 번째 연도 */
.performance-table tbody .year-color-2 {
  background-color: #c8daf0 !important;
}

/* 14번 여백 행: 배경색 제거 */
.performance-table .no-background {
  background-color: transparent !important;
}

/* FC수익: 월별 셀은 기존 색상 유지, 합계는 빈칸 */
.performance-table .year-total-cell {
  font-weight: 700;
}

/* 14번 여백 행은 합계 열도 일반 셀처럼 표시 */
.performance-table .no-background.year-total-cell {
  background-color: transparent !important;
  font-weight: normal;
}

/* 7번, 18번: 음수는 파란색 */
.performance-table .negative-value {
  color: #0000FF !important;
  font-weight: 600;
}

/* 7번, 18번: 양수는 빨간색 */
.performance-table .positive-value {
  color: #FF0000 !important;
  font-weight: 600;
}

.performance-table .bg-highlight {
  background-color: #fffb00 !important;
}

.performance-table tbody td {
  background: #fff;
}

.performance-table tbody tr:nth-child(even) td {
  background: #fafbfd;
}

.performance-table tbody tr:nth-child(even) .fixed-no,
.performance-table tbody tr:nth-child(even) .fixed-item {
  background: #f0f3f7;
}


.performance-table .number-cell {
  min-width: 48px;
  font-weight: 600;
  color: #657080;
}

.performance-table .item-cell {
  font-weight: 600;
  text-align: left;
}

.performance-table .blank-row td {
  height: 28px;
}

.fc-profit-input {
  min-width: 50px;
  width: 63px;
  height: 21px;
  padding: 1px 1px;
  border: 1px solid #b8c6d8;
  border-radius: 3px;
  box-sizing: border-box;
  font-size: 12px;
  text-align: right;
}

.fc-profit-input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
}

/* 매장 목록 */
.store-list {
  margin-top: 20px;
  border: 1px solid #d9dfe7;
  border-radius: 4px;
  overflow: hidden;
}

.store-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 14px;
  background: #edf1f6;
  border-bottom: 1px solid #d9dfe7;
}

.store-list-header h4 {
  margin: 0;
  font-size: 14px;
}

.store-list-header span {
  color: #687486;
  font-size: 12px;
}

.store-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 14px;
  border-bottom: 1px solid #e6e9ee;
}

.store-row:last-child {
  border-bottom: none;
}

.store-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 150px;
}

.store-code {
  color: #778292;
  font-size: 11px;
}

.store-name strong {
  font-size: 13px;
}

.store-stats {
  display: flex;
  gap: 28px;
}

.store-stats > div {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.store-stats span {
  color: #737e8d;
  font-size: 11px;
}

.store-stats strong {
  font-size: 13px;
}

/* 데이터 없음 */
.empty-message {
  padding: 50px 20px;
  background: #fff;
  border: 1px solid #dce1e8;
  text-align: center;
  color: #7b8491;
  font-size: 14px;
}

.error-message {
  color: #b42318;
}

.page-footer {
  margin-top: 16px;
  color: #7b8491;
  font-size: 11px;
}


/* 반응형 */
@media (max-width: 900px) {
  .page-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .office-section {
    padding: 10px;
  }

  .store-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .store-stats {
    width: 100%;
    justify-content: space-between;
    gap: 10px;
  }
}

@media print {
  .performance-page {
    padding: 0;
    background: #fff;
  }

  .office-section {
    break-inside: avoid;
  }

  .table-scroll {
    overflow: visible;
  }
}
</style>